import React, { useState } from 'react'

import { useMutation, useQuery, useQueryClient } from 'react-query'

import services from '@services'
const { RestService } = services

import { FlexContainer, YSpacing } from '@components/common'
import { AutocompleteInput, Button } from '@components/common/form'
import Table from '@components/common/Table'
import { Label } from '@res/styledComponents/index'

import { ClientGartenLinkResponse, GartenParentCompaniesResponse } from '@types'

interface Props {
  accountId: string
}

const GartenIntegration: React.FC<Props> = ({ accountId }) => {
  const queryClient = useQueryClient()
  const [selectedCompany, setSelectedCompany] = useState<{
    id: string
    name: string
  } | null>(null)
  const [triggerMigration, setTriggerMigration] = useState(false)

  const linksQuery = useQuery(
    ['clientGartenLinks', accountId],
    async (): Promise<ClientGartenLinkResponse> => {
      const res = await RestService.get(
        '/client-reporting-programs/client-garten-links',
        {
          clientId: accountId,
        },
      )

      return res
    },
  )

  const linkMutation = useMutation(
    async (body: {
      clientId: string
      gartenParentCompanyId: string
      triggerMigration: boolean
    }) => {
      return RestService.post(
        '/client-reporting-programs/client-garten-links',
        body,
      )
    },
    {
      onSuccess: () => {
        setSelectedCompany(null)
        setTriggerMigration(false)
        queryClient.invalidateQueries(['clientGartenLinks', accountId])
      },
      onError: () => {
        services.UIService.flashError(
          'Failed to link Garten company. Please try again.',
        )
      },
    },
  )

  const unlinkMutation = useMutation(
    async (linkId: string) => {
      return RestService.delete(
        `/client-reporting-programs/client-garten-links/${linkId}`,
      )
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clientGartenLinks', accountId])
      },
      onError: () => {
        services.UIService.flashError(
          'Failed to remove Garten link. Please try again.',
        )
      },
    },
  )

  const links = linksQuery.data?.links || []

  const linkedCompanyIds = new Set(links.map((l) => l.gartenParentCompanyId))

  const loadCompanies = async (req: { search: string }) => {
    const res: GartenParentCompaniesResponse = await RestService.get(
      '/client-reporting-programs/garten-parent-companies',
      req,
    )

    return (res.companies || []).filter((c) => !linkedCompanyIds.has(c.id))
  }

  const handleLink = () => {
    if (!selectedCompany) {
      return
    }
    linkMutation.mutate({
      clientId: accountId,
      gartenParentCompanyId: selectedCompany.id,
      triggerMigration,
    })
  }

  const isLoading =
    linksQuery.isLoading || linkMutation.isLoading || unlinkMutation.isLoading

  return (
    <FlexContainer flexDirection="column">
      <YSpacing height="20px" />
      {linksQuery.isLoading ? (
        <p>Loading...</p>
      ) : linksQuery.isError ? (
        <p>Error loading Garten links. Please refresh.</p>
      ) : (
        <Table
          headings={[
            'Company Name',
            'Garten Parent Company ID',
            'Linked At',
            '',
          ]}
        >
          {links.length === 0 ? (
            <tr>
              <td colSpan={4}>No Garten companies linked.</td>
            </tr>
          ) : (
            links.map((link) => (
              <tr key={link.id}>
                <td>{link.gartenParentCompanyName}</td>
                <td>{link.gartenParentCompanyId}</td>
                <td>{new Date(link.createdAt).toLocaleDateString()}</td>
                <td>
                  <Button
                    label="Remove"
                    onClick={() => unlinkMutation.mutate(link.id)}
                    disabled={isLoading}
                  />
                </td>
              </tr>
            ))
          )}
        </Table>
      )}
      <YSpacing height="30px" />
      <FlexContainer
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <FlexContainer width="300px" flexDirection="column">
          <Label style={{ display: 'block', marginBottom: 4 }}>
            Link Garten Parent Company
          </Label>
          <AutocompleteInput
            value={selectedCompany?.name || ''}
            width="100%"
            displayAttribute="name"
            loaderFunction={loadCompanies}
            onSelect={(company: { id: string; name: string }) =>
              setSelectedCompany(company)
            }
            searchDelay={300}
          />
        </FlexContainer>
        <YSpacing height="12px" />
        <FlexContainer flexDirection="row" alignItems="center" width="auto">
          <input
            type="checkbox"
            id="garten-trigger-migration"
            checked={triggerMigration}
            onChange={(e) => setTriggerMigration(e.target.checked)}
          />
          <Label
            htmlFor="garten-trigger-migration"
            style={{ marginLeft: 8, marginBottom: 0 }}
          >
            Trigger user migration
          </Label>
        </FlexContainer>
        <YSpacing height="12px" />
        <Button
          label="Link Company"
          onClick={handleLink}
          disabled={!selectedCompany || isLoading}
        />
      </FlexContainer>
    </FlexContainer>
  )
}

export default GartenIntegration
